import {
	AdAccountDTO,
	FacebookAdsCampaignDTO,
	GoogleAdsCampaignDTO,
	ProtectionInfoDTO
} from 'api-models'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { GLoading, GTab, GTabItem } from 'components/basic-blocks'
import { PageLayout } from 'components/page-layout'
import { PageTitle } from 'utils/page-title'

import { getLevel } from 'layout/navbar/breadcrumbs/utils'
import { useAccountStore, useAssetStore } from 'store'
import { getEntityName } from 'utils/get-entity-name'
import { useShallow } from 'zustand/react/shallow'
import { Notices } from '../../../../components/notice/notices'
import { useAsset } from '../asset-base'
import { useSearchParams } from 'react-router-dom'
import { RulesPanel } from './components/rules-panel'
import { ProtectionPanel } from './components/protection-panel'
import { RulesAnalyze } from './components/rules-panel/components/rules-analyze/rules-analyze'
import { useTrackPostHogEvent } from '../../../../utils/posthog'
import { PostHogEvent, PostHogPath } from '../../../../utils/posthog-events'
import { useFeatureFlag } from '../../../../services/feature-flags'
import { useFilterPersistence } from '../../../../hooks/use-filter-presistence'
import { useBreadcrumbSelection } from 'components/breadcrumb-bar'

export const ProtectionPage = () => {
	useFilterPersistence()
	const { accountId, assetId } = useAsset()
	const [searchParams] = useSearchParams()
	const { firstAdAccountId, firstCampaignId } = useBreadcrumbSelection()
	const campaignId = firstCampaignId
	const tabParam = searchParams.get('tab')
	const { asset, demoMode, campaigns, adAccounts } = useAssetStore(
		useShallow((s) => ({
			asset: s.asset,
			demoMode: s.demoMode,
			campaigns: s.campaigns,
			adAccounts: s.adAccounts
		}))
	)
	const { account, isInSetup, hasFeature } = useAccountStore(
		useShallow((s) => ({
			account: s.account,
			isInSetup: s.isInSetup,
			hasFeature: s.hasFeature
		}))
	)
	const trackPostHogEvent = useTrackPostHogEvent()
	const isCustomRulesAnalyzerEnabled = useFeatureFlag('custom_rules_analyser')

	const [selectedAdAccount, setSelectedAdAccount] =
		useState<AdAccountDTO | null>(null)
	const [selectedCampaign, setSelectedCampaign] = useState<
		GoogleAdsCampaignDTO | FacebookAdsCampaignDTO | null
	>(null)
	const [loading, setLoading] = useState(true)
	const [protection, setProtection] = useState<
		ProtectionInfoDTO | null | undefined
	>(null)
	const [activeTab, setActiveTab] = useState<'protection' | 'analysis'>(
		tabParam === 'analysis' && isCustomRulesAnalyzerEnabled
			? 'analysis'
			: 'protection'
	)
	const [renderedTabs, setRenderedTabs] = useState<Set<string>>(
		new Set([activeTab])
	)
	const hasInitializedAdAccount = useRef(false)
	const lastSyncedAdAccountIdRef = useRef<string | null>(null)
	const isInitializingAdAccountRef = useRef(false)
	const urlSyncTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	const lastProcessedUrlValueRef = useRef<string | null>(null)
	const lastProcessedCampaignIdRef = useRef<string | null>(null)

	const tabs = useMemo<GTabItem[]>(() => {
		const baseTabs: GTabItem[] = [{ key: 'protection', label: 'Protection' }]
		if (isCustomRulesAnalyzerEnabled) {
			baseTabs.push({
				key: 'analysis',
				label: 'Analysis'
			})
		}
		return baseTabs
	}, [isCustomRulesAnalyzerEnabled])

	const level = getLevel({
		isAdAccountSelected: !!selectedAdAccount,
		isCampaignSelected: !!selectedCampaign
	})
	const target =
		level === 'website'
			? asset
			: level === 'ad-account'
			? selectedAdAccount
			: selectedCampaign

	useEffect(() => {
		return () => {
			if (urlSyncTimeoutRef.current) {
				clearTimeout(urlSyncTimeoutRef.current)
				urlSyncTimeoutRef.current = null
			}
		}
	}, [])

	useEffect(() => {
		if (target) {
			setProtection(target.protection)
			setLoading(false)
		}
	}, [target])

	useEffect(() => {
		if (adAccounts.length === 0) {
			return
		}

		const isFirstRun = !hasInitializedAdAccount.current
		if (isFirstRun) {
			isInitializingAdAccountRef.current = true
		}

		let preSelectedAdAccountId: string | null = firstAdAccountId

		if (!preSelectedAdAccountId) {
			try {
				const stored = sessionStorage.getItem('clickguard_persisted_filters')
				if (stored) {
					const persistedFilters = JSON.parse(stored)
					if (persistedFilters.breadcrumbs?.adAccounts) {
						const accountIds =
							persistedFilters.breadcrumbs.adAccounts.split(',')
						preSelectedAdAccountId = accountIds[0]
					}
				}
			} catch (error) {
				console.error('Error getting persisted filters from storage:', error)
			}
		}

		if (
			preSelectedAdAccountId &&
			lastProcessedUrlValueRef.current !== preSelectedAdAccountId
		) {
			const adAccountSelected =
				adAccounts.find((adAccount) => {
					return adAccount.id === preSelectedAdAccountId
				}) || null
			if (adAccountSelected) {
				setSelectedAdAccount(adAccountSelected)
				lastSyncedAdAccountIdRef.current = adAccountSelected.id
				lastProcessedUrlValueRef.current = preSelectedAdAccountId
			}
		} else if (
			!preSelectedAdAccountId &&
			lastProcessedUrlValueRef.current !== null
		) {
			setSelectedAdAccount(null)
			lastSyncedAdAccountIdRef.current = null
			lastProcessedUrlValueRef.current = null
		}

		if (isFirstRun) {
			hasInitializedAdAccount.current = true
		}
	}, [adAccounts, firstAdAccountId])

	useEffect(() => {
		const preSelectedCampaignId = campaignId

		if (!preSelectedCampaignId) {
			if (
				lastProcessedCampaignIdRef.current !== null ||
				selectedCampaign !== null
			) {
				setSelectedCampaign(null)
				lastProcessedCampaignIdRef.current = null
			}
			return
		}

		if (campaigns.length === 0) {
			return
		}

		const campaignSelected =
			campaigns.find((campaign) => campaign.id === preSelectedCampaignId) ||
			null

		if (campaignSelected) {
			if (selectedCampaign !== campaignSelected) {
				setSelectedCampaign(campaignSelected)
			}

			if (selectedAdAccount?.id !== campaignSelected.ad_account_id) {
				const matchingAdAccount = adAccounts.find(
					(acc) => acc.id === campaignSelected.ad_account_id
				)
				if (matchingAdAccount) {
					setSelectedAdAccount(matchingAdAccount)
				}
			}
		} else if (selectedCampaign !== null) {
			setSelectedCampaign(null)
		}

		lastProcessedCampaignIdRef.current = preSelectedCampaignId
	}, [
		campaigns,
		campaignId,
		selectedAdAccount?.id,
		selectedCampaign,
		adAccounts
	])

	useLayoutEffect(() => {
		if (isInitializingAdAccountRef.current && hasInitializedAdAccount.current) {
			isInitializingAdAccountRef.current = false
		}
	}, [selectedAdAccount])

	// No URL write-back — breadcrumb owns the URL params

	useEffect(() => {
		if (!isCustomRulesAnalyzerEnabled && activeTab === 'analysis') {
			setActiveTab('protection')
		}
	}, [isCustomRulesAnalyzerEnabled, activeTab])

	useEffect(() => {
		if (tabParam === 'analysis' && isCustomRulesAnalyzerEnabled) {
			setActiveTab('analysis')
			setRenderedTabs((prev) => new Set(prev).add('analysis'))
		}
	}, [tabParam, isCustomRulesAnalyzerEnabled])

	return (
		<>
			<PageTitle pageName="AI + Custom Rules" />
			<PageLayout
				title={'AI + Custom Rules'}
				tagline="Traffic protection settings for"
				tags={[
					{
						label: getEntityName({
							selectedCampaign,
							adAccount: selectedAdAccount,
							asset,
							demoMode
						})
					}
				]}
				notices={
					<Notices
						isInSetup={isInSetup()}
						setupStep={account?.setup?.step}
						isInActive={account?.status === 'INACTIVE'}
						hasDashboardPerformanceFeature={hasFeature('dashboard_performance')}
						tc_installed={!!asset?.tc_installed}
						cc_installed={!!asset?.cc_installed}
						tc_installed_skipped={!!asset?.tc_installed_skipped}
						accountId={accountId}
						assetId={assetId}
						demoMode={demoMode}
						mccLinkInviteAdAccounts={adAccounts}
					/>
				}
			>
				{loading ? (
					<GLoading />
				) : (
					<main className="flex flex-col gap-3 w-full">
						<GTab
							tabs={tabs}
							activeTab={activeTab}
							onTabChange={(key) => {
								setActiveTab(key as 'protection' | 'analysis')
								setRenderedTabs((prev) => new Set(prev).add(key))
								if (key === 'analysis') {
									trackPostHogEvent(
										PostHogEvent.CLICK_ANALYZE_TAB,
										PostHogPath.AI_AND_CUSTOMRULES,
										null
									)
								}
							}}
						/>
						{renderedTabs.has('protection') && (
							<div className={activeTab === 'protection' ? 'block' : 'hidden'}>
								<div className="flex flex-col xl:flex-row xl:gap-3 w-full">
									<ProtectionPanel
										level={level}
										protection={protection}
										target={target}
										inheritName={
											level === 'ad-account'
												? asset?.name
												: selectedAdAccount?.name
										}
										platform={selectedAdAccount?.platform}
									/>
									<RulesPanel
										level={level}
										targetId={target?.id}
										targetName={target?.name}
										platform={selectedAdAccount?.platform}
										activeTab={activeTab}
									/>
								</div>
							</div>
						)}
						{isCustomRulesAnalyzerEnabled && renderedTabs.has('analysis') && (
							<div className={activeTab === 'analysis' ? 'block' : 'hidden'}>
								<RulesAnalyze
									level={level}
									targetId={target?.id}
									activeTab={activeTab}
									targetName={target?.name}
									platform={selectedAdAccount?.platform}
								/>
							</div>
						)}
					</main>
				)}
			</PageLayout>
		</>
	)
}
